import React, { useState, useEffect, useCallback, useRef } from 'react';
import { selectLoader } from '../../../utils/Helpers';
import search from '../../../assets/img/search.svg';
import APIServices from '../../../services/APIServices';
import { useAuth } from '../../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';

const SelectTemplate = ({ setTemplateId, setMilestoneTab, companyId, isLibrary = false, backUrl, showKbtemplates = true }) => {
  const { user } = useAuth();
  const listRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = location.state?.currentTab;
  // State
  const [activeTab, setActiveTab] = useState('custom');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [basicTemplates, setBasicTemplates] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [kanbanTemplates, setKanbanTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);

  // Pagination state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (currentTab) {
      console.log(currentTab, 'currentTab');

      setActiveTab(currentTab);
      navigate('/templates', {
        state: {
          backUrl
        }
      });
    }
  }, [currentTab]);

  // Random color generator
  const getRandomColor = () => {
    const colors = ['yallow-bg-color', 'overview-purple-bg', 'overview-background-bg'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  /** Fetch regular templates (custom/basic) */
  const fetchTemplates = async (companyId, searchText = '', pageNum = 1) => {
    try {
      setLoading(true);
      const response = searchText
        ? await APIServices.get(`/template/search/${companyId}?templateName=${searchText}&page=${pageNum}&pageSize=20`)
        : await APIServices.get(`/template/company/${companyId}?page=${pageNum}&pageSize=20`);

      if (response) {
        const templates = response.templates || [];
        if (pageNum === 1) {
          setCustomTemplates(templates);
        } else {
          setCustomTemplates((prev) => [...prev, ...templates]);
        }
        setHasMore(templates.length > 0); // stop when no more
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  /** Fetch Kanban templates */
  const fetchKanbanTemplates = async (searchText = '', pageNum = 1) => {
    if (activeTab === 'kanban' && user?.companyId?._id) {
      try {
        setLoading(true);
        const response = await APIServices.get(
          `/project-board-templates/company/${user?.companyId?._id}?page=${pageNum}&pageSize=20&search=${searchText}`
        );

        const templates = response?.templates || [];
        if (pageNum === 1) {
          setKanbanTemplates(templates);
        } else {
          setKanbanTemplates((prev) => [...prev, ...templates]);
        }
        setHasMore(templates.length > 0);
      } catch (error) {
        console.error('Error fetching Kanban templates:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  /** Fetch on tab/search change */
  // 🔎 Debounced search effect
  useEffect(() => {
    if (!companyId && activeTab !== 'kanban') return;

    const handler = setTimeout(() => {
      setPage(1);
      setHasMore(true);

      if (activeTab === 'kanban') {
        fetchKanbanTemplates(searchTerm, 1);
      } else if (companyId) {
        fetchTemplates(companyId, searchTerm, 1);
      }
    }, 400); // debounce delay

    // Cleanup if searchTerm/filters change before timeout finishes
    return () => clearTimeout(handler);
  }, [searchTerm, activeTab, companyId]);

  /** Update filtered templates */
  useEffect(() => {
    let templates = [];
    if (activeTab === 'basic') templates = basicTemplates;
    else if (activeTab === 'custom') templates = customTemplates;
    else if (activeTab === 'kanban') templates = kanbanTemplates;
    setFilteredTemplates(templates);
  }, [activeTab, basicTemplates, customTemplates, kanbanTemplates]);

  const handleScroll = useCallback(() => {
    if (loading || !hasMore || !listRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 20) {
      // ✅ Reached bottom
      const nextPage = page + 1;
      setPage(nextPage);
      if (activeTab === 'kanban') {
        fetchKanbanTemplates(searchTerm, nextPage);
      } else if (companyId) {
        fetchTemplates(companyId, searchTerm, nextPage);
      }
    }
  }, [loading, hasMore, page]);

  useEffect(() => {
    const container = listRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  /** Template select handler */
  const handleTemplateSelect = (templateId, isKanbanTemplate = false) => {
    setTemplateId(templateId);
    if (isKanbanTemplate) {
      navigate(`/project-templates/${templateId}`, {
        state: {
          backUrl
        }
      });
    } else {
      if (setMilestoneTab) {
        setMilestoneTab('add');
      }
    }
  };

  return (
    <div className={`choose-templates ${!isLibrary && 'mt-5'}`}>
      {!isLibrary && <h1 className="text-center mb-4">Choose a template</h1>}

      {/* Tabs */}
      <div className="d-flex justify-content-center">
        <div className="bg-white br-10 p-2 btn-tab-group">
          <button
            className={`btn btn-custom ${activeTab === 'custom' ? 'btn-dark' : 'btn-light'}`}
            onClick={() => {
              setActiveTab('custom');
              setSearchTerm('');
            }}>
            My Templates
          </button>
          <button
            className={`btn btn-custom ${activeTab === 'basic' ? 'btn-dark' : 'btn-light'}`}
            onClick={() => {
              setSearchTerm('');
              setActiveTab('basic');
            }}>
            Basic Templates
          </button>
          {showKbtemplates && <button
            className={`btn btn-custom ${activeTab === 'kanban' ? 'btn-dark' : 'btn-light'}`}
            onClick={() => {
              setSearchTerm('');
              setActiveTab('kanban');
            }}>
            Kanban Templates
          </button>}
        </div>
      </div>

      {/* Search */}
      <div className="input-group mt-4">
        <button className="input-group-text  border-0 fa-search-btn">
          <img src={search} alt="search" className="search-img" />
        </button>
        <input
          type="text"
          className="form-control ps-0"
          placeholder="Search"
          aria-label="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Templates */}
      <div className="row mt-4 templates-row" ref={listRef}>
        {filteredTemplates.length > 0
          ? filteredTemplates.map((template) => (
              <div key={template._id} className="col-md-6">
                <button
                  className={`btn btn-lg w-100 mb-3 position-relative text-left text-white hover ${getRandomColor()}`}
                  onClick={() => handleTemplateSelect(template._id, activeTab === 'kanban')}
                  aria-label={`Select ${template.name}`}>
                  {template.name}
                  {activeTab !== 'kanban' ? (
                    <p className="subtitle text-white">{template.milestoneCount} Milestones</p>
                  ) : (
                    <p className="subtitle text-white">Kanban Board</p>
                  )}
                  <span className="position-absolute translate-middle-y" style={{ top: '50%', right: '20px' }}>
                    <i className="fas fa-chevron-right"></i>
                  </span>
                </button>
              </div>
            ))
          : !loading && (
              <div className={`text-center mt-3 ${activeTab === 'basic' ? 'milestones-comming-soon' : ''}`}>
                <p>
                  {activeTab === 'basic'
                    ? 'Coming soon...'
                    : activeTab === 'kanban'
                    ? 'No Kanban templates found.'
                    : 'No templates found.'}
                </p>
              </div>
            )}
      </div>

      {loading && <div className="text-center mt-3">{selectLoader(50)}</div>}
    </div>
  );
};

export default SelectTemplate;
